// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactUsButton from "@mcmc/App/Components/ContactUsButton";
import { ContactUsButtonStyles } from "@mcmc/App/Components/ContactUsButton/ContactUsButton";
import RegisterEventButton from "@mcmc/App/Components/RegisterEventButton";
import { color, printStyles } from "@mcmc/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps;

export interface ProductDetailsActionsStyles {
    wrapper?: InjectableCss;
    contactUsMessage?: TypographyPresentationProps;
    contactUsButtonStyles?: ContactUsButtonStyles;
    registerEventButtonStyles?: ButtonPresentationProps;
}

export const actionsStyles: ProductDetailsActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            border: 1px solid ${color("common.border", 0.4)};
            border-left: none;
            border-right: none;
            margin: 20px 0;
            padding: 5px 0;
            ${printStyles.hideOnPrint}
        `,
    },
    contactUsMessage: {
        size: 14,
        lineHeight: "18px",
        css: css`
            display: block;
            letter-spacing: 0.2px;
            max-width: 350px;
            margin: 5px 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-width: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    contactUsButtonStyles: {
        contactUsButton: {
            variant: "secondary",
            sizeVariant: "medium",
            typographyProps: {
                size: 14,
            },
            css: css`
                min-width: 155px;
                margin: 5px 10px 5px 0;
            `,
        },
    },
    registerEventButtonStyles: {
        variant: "primary",
        sizeVariant: "medium",
        typographyProps: {
            size: 14,
        },
        css: css`
            min-width: 155px;
            margin: 5px 10px 5px 0;
        `,
    },
};

const styles = actionsStyles;

const ProductDetailsActions: React.FC<Props> = () => {
    return (
        <StyledWrapper {...styles.wrapper}>
            <RegisterEventButton extendedStyles={styles.registerEventButtonStyles} />
            <ContactUsButton extendedStyles={styles.contactUsButtonStyles} />
            <Typography {...styles.contactUsMessage}>
                {translate("If you have any questions about this training course, please feel free to contact us.")}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsActions,
    definition: {
        displayName: "Event Actions",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
